import { useQuery } from '@tanstack/react-query';
import { Database, Server, Bot, Globe } from 'lucide-react';
import { getHealth, listAlerts, listScrapers } from '@/api/observe';
import { cn } from '@/lib/cn';
import { relativeTime, txt } from '@/lib/format';
import SeverityBadge from '@/components/SeverityBadge';
import RuntimeCard from '@/components/RuntimeCard';

/**
 * Top-level overview. Three rows:
 *   1. Infra tiles  — Mongo / Redis / API reachability
 *   2. Stat counters — open critical alerts, blocked scrapers, healer actions/h
 *   3. Bot & API responsiveness — last hour of event-loop waits + bot timings
 *   4. Recent alerts — top 5 most-recent so the dashboard's first paint
 *      already shows what's going on right now.
 */
export default function Health() {
  const health   = useQuery({ queryKey: ['health'],   queryFn: getHealth,   refetchInterval: 5_000 });
  const alerts   = useQuery({ queryKey: ['alerts', { limit: 5 }],   queryFn: () => listAlerts({ limit: 5 }), refetchInterval: 5_000 });
  const scrapers = useQuery({ queryKey: ['scrapers'], queryFn: listScrapers, refetchInterval: 15_000 });

  const mongoOk = health.data?.services.mongodb.healthy;
  const redisOk = health.data?.services.redis.healthy;
  const apiOk   = !health.isError;

  const allAlerts = alerts.data?.alerts ?? [];
  const criticalCount = allAlerts.filter(a => a.severity === 'critical').length;
  const blockedScrapers = (scrapers.data?.scrapers ?? []).filter(
    s => s.last_run_status === 'blocked',
  ).length;

  return (
    <div className="space-y-6 max-w-6xl">
      <header>
        <h1 className="text-xl font-semibold">System Health</h1>
        <p className="text-sm text-slate-500 mt-1">
          Real-time view of infra + scrapers. Updates every 5 seconds.
        </p>
      </header>

      {/* Infra tiles */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <InfraTile icon={<Database className="w-4 h-4" />} label="MongoDB"  ok={mongoOk}
          detail={health.data?.services.mongodb.lastError} />
        <InfraTile icon={<Server   className="w-4 h-4" />} label="Redis"    ok={redisOk}
          detail={health.data?.services.redis.lastError} />
        <InfraTile icon={<Globe    className="w-4 h-4" />} label="otr-core API" ok={apiOk}
          detail={health.isError ? 'unreachable' : undefined} />
      </section>

      {/* Stat counters */}
      <section className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <CounterTile label="Critical alerts (recent)" value={criticalCount} tone={criticalCount > 0 ? 'bad' : 'ok'} />
        <CounterTile label="Blocked scrapers"         value={blockedScrapers} tone={blockedScrapers > 0 ? 'bad' : 'ok'} />
        <CounterTile label="Total scrapers"           value={scrapers.data?.scrapers.length ?? 0} />
      </section>

      {/* Bot & API responsiveness */}
      <RuntimeCard />

      {/* Recent alerts */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <Bot className="w-4 h-4 text-slate-500" />
            Recent alerts
          </h2>
          <a href="/alerts" className="text-xs text-emerald-400 hover:text-emerald-300">View all →</a>
        </div>
        {alerts.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
        {!alerts.isLoading && allAlerts.length === 0 && (
          <div className="text-sm text-slate-500">No alerts. Quiet skies.</div>
        )}
        <ul className="divide-y divide-slate-800/60">
          {allAlerts.map(a => (
            <li key={a.id} className="py-2 flex items-start gap-2 sm:gap-3">
              <SeverityBadge severity={a.severity} />
              <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="font-mono text-xs text-slate-500 shrink-0">{txt(a.category)}</span>
                <span className="text-sm break-words min-w-0">{a.message}</span>
              </div>
              <span className="text-xs text-slate-500 shrink-0 whitespace-nowrap mt-0.5">{relativeTime(a.ts)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function InfraTile({ icon, label, ok, detail }: {
  icon: React.ReactNode;
  label: string;
  ok: boolean | null | undefined;
  detail?: string;
}) {
  const tone = ok === true ? 'ok' : ok === false ? 'bad' : 'pending';
  return (
    <div className={cn(
      'rounded-lg border bg-slate-900/40 p-4',
      tone === 'ok'      && 'border-emerald-500/30',
      tone === 'bad'     && 'border-red-500/40',
      tone === 'pending' && 'border-slate-800',
    )}>
      <div className="flex items-center gap-2 text-xs text-slate-400">
        {icon}
        <span>{label}</span>
      </div>
      <div className={cn(
        'mt-2 text-2xl font-semibold',
        tone === 'ok'  && 'text-emerald-300',
        tone === 'bad' && 'text-red-300',
        tone === 'pending' && 'text-slate-400',
      )}>
        {tone === 'ok' ? 'OK' : tone === 'bad' ? 'DOWN' : 'PROBING'}
      </div>
      {detail && <div className="mt-1 text-xs text-slate-500 truncate">{detail}</div>}
    </div>
  );
}

function CounterTile({ label, value, tone = 'neutral' }: {
  label: string;
  value: number;
  tone?: 'ok' | 'bad' | 'neutral';
}) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={cn(
        'mt-1 text-2xl font-semibold',
        tone === 'ok'  && 'text-emerald-300',
        tone === 'bad' && 'text-red-300',
        tone === 'neutral' && 'text-slate-200',
      )}>
        {value}
      </div>
    </div>
  );
}
